import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { handleBaseTwoFactorErrors } from '@pages/Profile/AccountSettings/handleBaseTwoFactorErrors'
import { Resend2FAError } from '@pages/TwoFactorAuth/TwoFactorAuthPage'
import { TwoFactorErrors } from '@pages/TwoFactorAuth/useTwoFactorAuth'

import { useSnackbar } from '@context/SnackbarContext'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import type { MasterTransaction } from '@model/MasterTransaction'
import { TwoFAProviderEnum } from '@model/TwoFactorAuthentication'
import type { TwoFactor } from '@model/User'

import { useFundingNav } from '@hooks/useFundingNav'

// import { useWithdrawalTracker } from '@analytics/amplitude/withdrawal'

import type { WithdrawalTwoFactorLocationState } from '../../WithdrawalFlowLayout/types'

export const useWithdrawalTwoFactor = (locationState?: WithdrawalTwoFactorLocationState) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { go } = useFundingNav()
  // const tracker = useWithdrawalTracker()
  const { addSnackbar } = useSnackbar()

  const withdrawalPayload = locationState?.withdrawalPayload
  const initialTwoFactor = locationState?.transaction?.twoFactor?.[0] ?? undefined

  const [twoFactorData, setTwoFactorData] = useState<TwoFactor | undefined>(initialTwoFactor)
  const [error, setError] = useState<string | undefined>()
  const [status, setStatus] = useState<'success' | 'error' | undefined>()
  const [resendError, setResendError] = useState<Resend2FAError>({
    hideResend: false,
    errorTimer: 0,
    tooManyInputs: false,
  })
  const [disableResend, setDisableResend] = useState(false)
  const [initialCountdown, setInitialCountdown] = useState(30)

  const isGoogleAuth =
    twoFactorData?.provider?.id === TwoFAProviderEnum.GoogleAuthenticator ||
    !twoFactorData?.authMedium

  const navigateToSuccess = useCallback(
    (tx: MasterTransaction) => {
      // tracker.submitted({ withdrawal_id: tx.id })
      go('../transaction/success', { state: { transaction: tx } })
    },
    [go]
  )

  const handleSubmitWithCode = useCallback(
    async (code: string) => {
      if (!twoFactorData || !withdrawalPayload) return

      setError(undefined)

      try {
        const tx = await apiClient.transactionWithdraw({
          ...withdrawalPayload,
          twoFactor: {
            code,
            token: twoFactorData.token,
            typeId: twoFactorData.provider.id,
          },
        })

        queryClient.invalidateQueries({ queryKey: ['wallet'] })
        queryClient.invalidateQueries({ queryKey: ['transaction'] })

        setStatus('success')
        navigateToSuccess(tx)
        return
      } catch (e: unknown) {
        const responseError = e as ResponseError
        const errorData = responseError?.response?.response?.data
        const errorCode = errorData?.code

        if (errorCode === TwoFactorErrors.TooManyInputs) {
          setError(
            t('TwoFactorAuth.2FA code attempts reached. Please resend a new code and try again')
          )
        } else if (
          errorCode === TwoFactorErrors.TooManyAttempts ||
          errorCode === TwoFactorErrors.TooManySends
        ) {
          navigate('/profile/security-settings/2fa', {
            state: {
              isTooManyResendAttempts: true,
              cooldownInMinutes: errorData?.properties?.cooldownInMinutes,
            },
          })
          return
        } else if (
          errorCode === TwoFactorErrors.InvalidToken ||
          errorCode === TwoFactorErrors.TokenExpired
        ) {
          handleBaseTwoFactorErrors(e, navigate)
          return
        } else {
          setError(t('Validation.Wrong code Verification failed'))
        }

        throw new Error('2fa_failed')
      }
    },
    [apiClient, navigate, navigateToSuccess, queryClient, t, twoFactorData, withdrawalPayload]
  )

  const resendCode = useCallback(async () => {
    if (!twoFactorData) return

    setDisableResend(true)
    try {
      const response = await apiClient.resend2FACode({
        typeId: twoFactorData.provider.id,
        token: twoFactorData.token,
      })

      if (Array.isArray(response) && response.length > 0) {
        setTwoFactorData(response[0])
        setInitialCountdown(30)
        setError(undefined)
        if (resendError.tooManyInputs) {
          setResendError({ errorTimer: 0, hideResend: false, tooManyInputs: false })
        }
        addSnackbar.success({ message: t('Sign up.Your new code has been sent') })
      }
    } catch (e: unknown) {
      const responseError = e as ResponseError
      const errorData = responseError?.response?.response?.data
      const errorCode = errorData?.code

      if (
        errorCode === TwoFactorErrors.TooManyAttempts ||
        errorCode === TwoFactorErrors.TooManySends
      ) {
        navigate('/profile/security-settings/2fa', {
          state: {
            isTooManyResendAttempts: true,
            cooldownInMinutes: errorData?.properties?.cooldownInMinutes,
          },
        })
        return
      }

      handleBaseTwoFactorErrors(e, navigate)
    } finally {
      setDisableResend(false)
    }
  }, [addSnackbar, apiClient, navigate, resendError.tooManyInputs, t, twoFactorData])

  return {
    isGoogleAuth,
    twoFactorData,
    error,
    status,
    resendError,
    disableResend,
    initialCountdown,
    setInitialCountdown,
    handleSubmitWithCode,
    resendCode,
  }
}
